import moment from "moment-timezone";
import { MetaBetLink } from "src/services/OfferService";
import AnalyticsService from "src/services/AnalyticsService";
import { currentSport } from "src/services/ApolloService";
import { renderRating } from "src/services/ValueDataService";
import PlayerImage from "../Player/PlayerImage";
import styles from "./styles.scss";
import { UpgradeButton } from "../UpgradeButton";
import React, { useContext, useMemo, useRef, useState } from "react";
import { SubscriptionContext } from "src/services/SubscriptionService";
import Icon from "@mdi/react";
import { mdiCalendarText } from "@mdi/js";
import FlexPanel from "../FlexPanel";
import TeamLogo from "../Team/TeamLogo";
import Tooltip from "../Tooltip";
import { printPlus } from "src/services/DisplayService";
import { PLAYER_PROPS_QUERY } from "src/gql/queries";
import { useQuery, useReactiveVar } from "@apollo/client";
import { rfc3986EncodeURIComponent } from "src/services/UtilService";
import { BookIcon } from "../BookImage";
import { useFreeTrialClaimed } from "src/services/UserService";
import { STATE_MAP } from "src/constants";
import { PlayerCardButton } from "../PlayerCard/PlayerCardButton";
import { PlayerCardName } from "../PlayerCard/PlayerCardName";
import { Lock } from "src/components/Lock";

// Assign each prop to its row
const generatePropRows = (props: any[]) => {
  let propRows: any[][] = [];
  props.forEach((prop) => {
    const row = prop.propRow;
    if (!propRows[row]) {
      propRows[row] = [];
    }
    propRows[row].push(prop);
  });
  propRows = propRows.filter((row) => row.length);
  return propRows;
};

type PlayerPropsProps = {
  event: any;
  playerProps: any;
};

export const PlayerProps = ({ event, playerProps }: PlayerPropsProps) => {
  const trialClaimed = useFreeTrialClaimed();
  const ctx = useContext(SubscriptionContext);
  const sport = useReactiveVar(currentSport);
  const legalized = STATE_MAP[ctx.userLocale]?.legal;
  const { player, props } = playerProps;
  const hasAccess = props[0].projectedValue != null;
  const hasBestBets = props.some((prop: any) => prop.book);
  const propRows = generatePropRows(props);
  const newsRef = useRef<any>();
  const [newsOpen, setNewsOpen] = useState(false);
  const showLiveNewsDot = useMemo(() => {
    return moment(player.news?.newsTime ?? undefined).isAfter(
      sport?.hasConfiguredSchedule ? moment().subtract(7, "days") : moment().subtract(24, "hours")
    );
  }, [player.news?.newsTime]);
  return (
    <div className="player-props">
      <PlayerImage url={player.imageUrl} containerClassName="player-image d-none d-sm-block" />
      <div className="player-props-body">
        <div className="player-props-titlebar">
          <PlayerImage url={player.imageUrl} containerClassName="player-image d-sm-none" />
          <div className="player-props-titlebar-right">
            <PlayerCardButton className="player-name" player={player}>
              <PlayerCardName>{player.fullName}</PlayerCardName>&nbsp;&nbsp;{player.position}
            </PlayerCardButton>
            {Boolean(player.injuryStatus) && <div className="player-injury-status">{player.injuryStatus}</div>}
            {Boolean(player.news?.analysis) && (
              <div className="player-news-toggle" onClick={() => setNewsOpen(!newsOpen)}>
                <Icon path={mdiCalendarText} size="16px" color="#004198" />
                Latest News
                {showLiveNewsDot && <div className="player-news-live-dot" />}
              </div>
            )}
          </div>
        </div>
        <div className="prop-section-divider" />
        {Boolean(player.news?.analysis) && (
          <React.Fragment>
            <div
              className="player-news"
              style={{
                height: newsOpen ? newsRef.current?.scrollHeight ?? 0 : 0,
                overflow: "hidden",
                transition: "all .3s",
              }}
              ref={newsRef}
            >
              <div className="player-news-date">Updated {moment(player.news.newsTime).calendar()}</div>
              <div className="player-news-analysis">{player.news.analysis}</div>
            </div>
            <div className="prop-section-divider" />
          </React.Fragment>
        )}
        {hasAccess ? (
          <div className="prop-section">Projections</div>
        ) : (
          <div className="prop-section">Upgrade to see projections from 10,000 simulations.</div>
        )}
        <div className="prop-projections">
          {/* 3 items per row */}
          {propRows.map((row: any, i: any) => (
            <div className="prop-projection-row" key={i}>
              {row.map((prop: any, j: any) => (
                <div className="prop-projection" key={j}>
                  {hasAccess ? (
                    <span className="prop-projected-value">{prop.projectedValue}</span>
                  ) : (
                    <Lock className="prop-projected-value" />
                  )}
                  <span className="prop-projected-abbr"> {prop.propAbbreviation}</span>
                  {j !== row.length - 1 ? <span>,&nbsp;</span> : null}
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="prop-section-divider" />
        {hasBestBets ? <div className="prop-section">Best Bets</div> : null}
        {hasBestBets
          ? props.map((prop: any, idx: any) => {
              return (
                prop.book &&
                prop.bookValue !== prop.projectedValue && (
                  <div key={idx} className="prop-bet">
                    {!hasAccess && <Lock />}
                    <BookIcon book={prop.book} style={{ height: 20, paddingRight: 0 }} />
                    <div className="prop-bet-text">
                      {hasAccess ? (prop.direction < 1 ? "Under" : "Over") : "Over/Under"} {prop.bookValue}{" "}
                      {prop.propName}
                      {hasAccess ? ` (${printPlus(prop.money)})` : null}{" "}
                      {legalized && prop.book && (
                        <MetaBetLink
                          className="prop-bet-now"
                          target="_blank"
                          rel="noopener"
                          onClick={() =>
                            AnalyticsService.track("Bet Now", {
                              page: window.location.href,
                              sport: sport?.name,
                              book: prop.book.name,
                              state: ctx.userLocale,
                              component: "PlayerProps",
                              playerName: player.fullName,
                              teamName: event.homeTeam.fullName,
                            })
                          }
                          bookName={prop.book.name}
                          sportsdataGameId={event.sportsdataGameId}
                        >
                          Bet Now
                        </MetaBetLink>
                      )}
                    </div>
                    {hasAccess && (
                      <div className="prop-stars">{renderRating({ displayRating: prop.stars }, false)}</div>
                    )}
                  </div>
                )
              );
            })
          : null}
        <div className="prop-section-divider" />
        {hasAccess ? null : (
          <UpgradeButton className="prop-upgrade-button" game={event} source="Player Props">
            {trialClaimed ? "Upgrade now" : "Start Free Trial"}
          </UpgradeButton>
        )}
      </div>
    </div>
  );
};

type TeamPlayerPropsProps = {
  event: any;
  team: any;
  loading: boolean;
};

export const TeamPlayerProps = ({ event, team: _team, loading }: TeamPlayerPropsProps) => {
  if (loading) {
    return null;
  }

  const teamPlayerProps = event[`${_team}PlayerProps`];
  const team = event[`${_team}Team`];

  const hasProps = teamPlayerProps.length;
  const hasBestBets = teamPlayerProps
    .map((p: any) => p.props)
    .flat()
    .some((p: any) => p.book);
  const hasAccess = !hasProps || teamPlayerProps[0].props[0].projectedValue != null;

  return (
    <div className="team-player-props">
      <div className="team-player-props-head">
        <div className="team-image">
          <TeamLogo
            containerStyle={{ display: "flex", alignItems: "center" }}
            leagueName={event.league.name}
            teamAbbreviation={team.preferredAbbreviation}
            size={44}
          />
        </div>
        <div className="team-player-props-head-right">
          <div className="team-name">{team.lastName} Player Prop Bets</div>
          {hasAccess ? null : (
            <div className="props-upgrade-message">
              Upgrade for all {event.awayTeam.preferredAbbreviation}-{event.homeTeam.preferredAbbreviation} best bets
              and projected stats.
            </div>
          )}
          {hasBestBets ? null : (
            <Tooltip
              title="Star Ratings"
              customIcon={<div className="props-stars-not-shown-tooltip">When will star ratings be available?</div>}
              tooltipWidth={280}
            >
              Best Bets with star ratings will display once sportsbooks post the player prop lines. Sportsbooks usually
              post the player prop lines at these times:
              <ul className="props-lines-ul">
                <li>NFL: 2 days before the game</li>
                <li>NBA: Day of the game, morning PST</li>
                <li>MLB: Day of the game, morning PST</li>
                <li>NHL: Day of the game, morning PST</li>
              </ul>
            </Tooltip>
          )}
        </div>
      </div>
      {hasProps ? (
        teamPlayerProps.map((playerProps: any, idx: any) => (
          <PlayerProps event={event} playerProps={playerProps} key={idx} />
        ))
      ) : (
        <div className="no-player-props">
          <span>Player Props will display once sportsbook data is provided.</span>
        </div>
      )}
      <style jsx>{styles}</style>
    </div>
  );
};

type PlayerPropsPanelProps = {
  slugId: string;
};

export const PlayerPropsPanel = ({ slugId }: PlayerPropsPanelProps) => {
  const { data, loading } = useQuery(PLAYER_PROPS_QUERY, {
    ssr: false,
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
    variables: {
      slugId: rfc3986EncodeURIComponent(decodeURIComponent(slugId as string)),
    },
  });

  const event = data?.event;

  return (
    <FlexPanel>
      <TeamPlayerProps event={event} loading={loading} team="away" />
      <TeamPlayerProps event={event} loading={loading} team="home" />
    </FlexPanel>
  );
};
